import Decimal from 'decimal.js';
import { environment } from '../../../../../environments/environment';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ComponentsModule } from '@app/shared/components/components.module';
import { CommonModule } from '@angular/common';
import { SharedModule } from '@app/shared/shared.module';
import { AdminUser } from '@app/shared/services/user.service';
import { ShipmentDetailsComponent } from '@app/pages/authenticated/components/shipment-details/shipment-details.component';
import { HelpersService } from '@app/shared/services/helpers.service';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { Animations, BillingTypes, BillingTypesVerbose, DateFormats } from '@app/shared/constants';
import { getUniuniRoutingBadge as resolveUniuniRoutingBadge } from '@app/shared/utils/uniuni-routing-badge.util';

@Component({
  selector: 'shipments-list',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './shipments-list.component.html',
  styleUrls: ['./shipments-list.component.scss'],
  imports: [ComponentsModule, CommonModule, SharedModule, ShipmentDetailsComponent],
  animations: [Animations.ExpandAnimation],
})
export class ShipmentsListComponent implements OnInit {
  @Input() shipments: any;
  @Input() user: AdminUser;
  @Input() hideProvider: boolean = false;
  @Input() showCourierInvoice: boolean = false;
  @Input() useCustomPagination: boolean = false;
  @Input() totalShipments: number;
  @Input() currentPage: number = 0;
  @Input() noCard: boolean = false;
  @Input() hideUselessPagination: boolean = false;
  @Input() isFinanceListing: boolean = true;
  @Input() showClaimCostWithTax: boolean = false;
  @Input() canEditClaimCost: boolean = false;
  // Opt-in only: the Finance "not invoiced by courier"/"not invoiced to customer" tabs turn this
  // on to surface the PostNL/Direct UniUni routing badge next to the courier name. Off by default
  // so the other consumers of this shared component (Shipments AWB search, Claims, invoiced
  // tabs) render unchanged.
  @Input() showUniuniRoutingBadge: boolean = false;
  @Output() editClaimCostEvent: EventEmitter<any> = new EventEmitter();
  @Output() pageChangedEvent: EventEmitter<PageEvent> = new EventEmitter();
  @Output() sortChangedEvent: EventEmitter<Sort> = new EventEmitter();
  @Output() refreshShipmentEvent: EventEmitter<any> = new EventEmitter();

  public loading: boolean = true;
  public paginatedShipments: any = [];
  public displayedColumns: string[] = [
    'select',
    'tracking',
    'provider_type',
    'status',
    'invoice',
    'hasCourierInvoice',
    'claimCustomerAmount',
    'claimMachoolAmount',
    'claimCreditAmount',
    'claimPaymentAmount',
    'cost',
    'revenue',
    'marginAmount',
    'marginPercent',
    'createdAt',
    'actions',
  ];
  public CDN_URL: string = environment.CDN_URL;
  public DATE_FORMAT: string = DateFormats.DATE_YEAR;
  public expandedElement = null;
  public sortedData: any = [];
  public itemsPerPage: number = 25;
  public itemsPerPageOptions: number[] = [25, 50, 100];

  constructor(private helpersService: HelpersService) {}

  ngOnInit() {
    this.loading = true;
    if (!this.showCourierInvoice) {
      this.displayedColumns = this.displayedColumns.filter((e) => e !== 'hasCourierInvoice');
    }
    if (this.hideProvider) {
      this.displayedColumns = this.displayedColumns.filter((e) => e !== 'provider_type');
    }
    if (!this.isFinanceListing) {
      this.displayedColumns = this.displayedColumns.filter(
        (e) => !['cost', 'revenue', 'marginAmount', 'marginPercent'].includes(e)
      );
    }
    if (!this.showClaimCostWithTax) {
      this.displayedColumns = this.displayedColumns.filter(
        (e) =>
          !['claimCustomerAmount', 'claimMachoolAmount', 'claimCreditAmount', 'claimPaymentAmount', 'actions'].includes(
            e
          )
      );
    }
    if (this.shipments) {
      this.shipments.forEach((shipment) => {
        shipment.statusVerbose = this.helpersService.convertLegacyShipmentStatusEnumToString(shipment.status);
        shipment.trackingStatusVerbose = shipment.trackingStatus
          ? this.helpersService.convertLegacyShipmentStatusEnumToString(shipment.trackingStatus)
          : 'Not set';
        if (shipment.paymentType === BillingTypes.MACHOOL_ACCOUNT) {
          shipment.invoices = shipment.adminShipmentInvoices.map((invoice) => invoice.invoiceNumber).join(', ');
        } else if (shipment.paymentType === BillingTypes.CREDIT_CARD) {
          shipment.invoices = BillingTypesVerbose.CREDIT_CARD;
        } else if (shipment.paymentType === BillingTypes.MACHOOL_WALLET) {
          shipment.invoices = BillingTypesVerbose.MACHOOL_WALLET;
        } else if (shipment.paymentType === BillingTypes.THIRDPARTY_ACCOUNT) {
          shipment.invoices = BillingTypesVerbose.THIRDPARTY_ACCOUNT;
        }

        shipment.providerName = shipment.isMultiLeg
          ? this.helpersService.convertShipmentProviderToString(shipment.shipmentLegs[0].provider)
          : this.helpersService.convertShipmentProviderToString(shipment.provider);

        const thirdPartyInsuranceAdminFee = shipment.thirdPartyInsurance?.adminFee
          ? new Decimal(shipment.thirdPartyInsurance?.adminFee)
          : new Decimal(0);

        const thirdPartyInsuranceTotal = shipment.thirdPartyInsurance?.totalAmount
          ? new Decimal(shipment.thirdPartyInsurance?.totalAmount)
          : new Decimal(0);

        const subTotalCustomerAmount = shipment.adminChargeDetails.customerCharges.reduce(
          (total, { baseAmount, fuelAmount, optionsAmount, surchargeAmount }) =>
            total
              .plus(new Decimal(baseAmount || 0))
              .plus(new Decimal(fuelAmount || 0))
              .plus(new Decimal(optionsAmount || 0))
              .plus(new Decimal(surchargeAmount || 0)),
          new Decimal(0)
        );

        const subtotalMachoolAmount = shipment.adminChargeDetails.machoolCharges.reduce(
          (total, { baseAmount, fuelAmount, optionsAmount, surchargeAmount }) =>
            total
              .plus(new Decimal(baseAmount || 0))
              .plus(new Decimal(fuelAmount || 0))
              .plus(new Decimal(optionsAmount || 0))
              .plus(new Decimal(surchargeAmount || 0)),
          new Decimal(0)
        );

        shipment.customerTotalAmount = thirdPartyInsuranceAdminFee
          .plus(thirdPartyInsuranceTotal)
          .plus(subTotalCustomerAmount)
          .toNumber();

        shipment.machoolTotalAmount = thirdPartyInsuranceTotal.plus(subtotalMachoolAmount).toNumber();

        shipment.marginAmount = new Decimal(shipment.customerTotalAmount)
          .minus(new Decimal(shipment.machoolTotalAmount))
          .toNumber();

        shipment.marginPercent = new Decimal(shipment.customerTotalAmount)
          .minus(new Decimal(shipment.machoolTotalAmount))
          .dividedBy(new Decimal(shipment.customerTotalAmount))
          .times(1000)
          .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
          .dividedBy(10)
          .toNumber();

        shipment.hasMAInvoice = shipment.invoices?.length > 0;
        shipment.hasCourierInvoice = shipment.ediInvoice ? 'Yes' : 'No';
      });

      if (!this.useCustomPagination) {
        this.pageChanged({
          pageIndex: 0,
          pageSize: this.itemsPerPage,
          length: this.totalShipments,
        });
      } else {
        this.paginatedShipments = this.shipments;
      }
      this.loading = false;
    }
  }

  public sortData(sort: Sort) {
    if (!this.useCustomPagination) {
      const data = this.shipments.slice();
      if (!sort.active || sort.direction === '') {
        this.sortedData = data;
        return;
      }
      const isAsc = sort.direction === 'asc';
      this.sortedData = data.sort((a, b) => this.helpersService.compare(a[sort.active], b[sort.active], isAsc));
      this.shipments = this.sortedData;
    } else {
      this.sortChangedEvent.emit(sort);
    }
    this.pageChanged({
      pageIndex: this.currentPage,
      pageSize: this.itemsPerPage,
      length: this.totalShipments,
    });
  }

  public toggleShipmentDetails(shipmentRow: any) {
    const sameRowClicked = this.expandedElement === shipmentRow;
    this.expandedElement = sameRowClicked ? null : shipmentRow;
  }

  /**
   * Feed badge for UniUni shipment rows, only rendered when showUniuniRoutingBadge is enabled.
   * See uniuni-routing-badge.util.ts for the PostNL/Direct/null semantics (shared with
   * /reconciliation's identical badge).
   */
  public getUniuniRoutingBadge(shipment: any): 'PostNL' | 'Direct' | null {
    return resolveUniuniRoutingBadge(shipment);
  }

  public pageChanged(pageEvent: PageEvent): void {
    if (this.useCustomPagination) {
      this.pageChangedEvent.emit(pageEvent);
    } else {
      this.loading = true;
      this.expandedElement = null;
      this.itemsPerPage = pageEvent.pageSize;
      this.currentPage = pageEvent.pageIndex;
      const end = this.itemsPerPage * (this.currentPage + 1);
      const start = end - this.itemsPerPage;
      this.paginatedShipments = this.shipments.slice(start, end);
      this.loading = false;
    }
  }
}
